import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Logo } from '@/components/ui/Logo';
import i18n from '@/i18n';
import { usePublicSite } from './queries';

/**
 * The couple's wedding site, as a guest sees it.
 *
 * Deliberately outside the app shell: no tab bar, no sign-in prompt, no mention
 * of Guabi beyond a small line at the bottom. A guest who arrives here is not a
 * user of the product and should not be sold one.
 *
 * **It renders in the couple's language, not the reader's.** The couple's own
 * words are already in their language; furniture around them in a different one
 * would be worse than either. So a Cartagena wedding reads Spanish on an English
 * phone, which is the whole point.
 */
export function PublicSitePage() {
  const { t } = useTranslation();
  const { slug = '' } = useParams();
  const site = usePublicSite(slug);
  const locale = site.data?.locale;

  useEffect(() => {
    if (locale && i18n.language !== locale) void i18n.changeLanguage(locale);
  }, [locale]);

  if (site.isLoading) {
    return <Shell>{null}</Shell>;
  }

  // One message for "no such address" and "not published yet", because the API
  // deliberately cannot tell them apart.
  if (site.isError || !site.data) {
    return (
      <Shell>
        <p className="text-body text-leaf-soft">{t('public.noPage')}</p>
      </Shell>
    );
  }

  const { title, partnerOneName, partnerTwoName, date, city, site: content } = site.data;
  const names = [partnerOneName, partnerTwoName].filter(Boolean).join(' & ') || title;

  return (
    <Shell>
      <header className="flex flex-col items-center gap-4 border-b border-line pb-12 text-center">
        <p className="eyebrow">{content.headline || t('public.defaultHeadline')}</p>
        <h1 className="font-display text-[44px] font-light leading-[1.05] md:text-[64px]">
          {names}
        </h1>
        {(date || city) && (
          <p className="tabular text-body text-leaf-soft">
            {[date && formatLongDate(date), city].filter(Boolean).join(' · ')}
          </p>
        )}
      </header>

      {content.story && <Section heading={t('public.story')} body={content.story} />}

      {content.events.length > 0 && (
        <section className="flex flex-col gap-6 border-b border-line py-12">
          <h2 className="font-display text-subhead">{t('public.theDay')}</h2>
          <ul className="flex flex-col gap-6">
            {content.events.map((event, i) => (
              <li key={`${event.name}-${i}`} className="flex flex-col gap-1">
                <span className="flex flex-wrap items-baseline gap-3">
                  <span className="text-section">{event.name}</span>
                  {event.startTime && (
                    <span className="tabular text-body text-leaf-soft">{event.startTime}</span>
                  )}
                </span>
                {event.venueName && <span className="text-body">{event.venueName}</span>}
                {event.address && <span className="text-body text-leaf-soft">{event.address}</span>}
                {event.note && <span className="text-caption text-leaf-soft">{event.note}</span>}
                {event.url && (
                  <a
                    href={event.url}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="min-h-tap text-caption text-brass-text underline underline-offset-4"
                  >
                    {t('public.directions')}
                  </a>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      {content.dressCode && <Section heading={t('public.dressCode')} body={content.dressCode} />}
      {content.travel && <Section heading={t('public.travel')} body={content.travel} />}

      {content.registry.length > 0 && (
        <section className="flex flex-col gap-4 border-b border-line py-12">
          <h2 className="font-display text-subhead">{t('public.gifts')}</h2>
          <ul className="flex flex-wrap gap-3">
            {content.registry.map((link) => (
              <li key={link.url}>
                <a
                  href={link.url}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="inline-flex min-h-tap items-center rounded-pill border border-line px-4 text-body hover:border-leaf-soft"
                >
                  {link.label}
                </a>
              </li>
            ))}
          </ul>
        </section>
      )}

      {content.faq.length > 0 && (
        <section className="flex flex-col gap-6 border-b border-line py-12">
          <h2 className="font-display text-subhead">{t('public.questions')}</h2>
          <dl className="flex flex-col gap-5">
            {content.faq.map((item, i) => (
              <div key={`${item.question}-${i}`} className="flex flex-col gap-1">
                <dt className="text-section">{item.question}</dt>
                <dd className="whitespace-pre-line text-body text-leaf-soft">{item.answer}</dd>
              </div>
            ))}
          </dl>
        </section>
      )}

      {/* No RSVP form here. The form needs a token, and a token belongs to one
          household — a form on the open page could only ever ask "who are you?",
          which is both a worse experience and a way to enumerate the guest list. */}
      <section className="flex flex-col gap-3 py-12">
        <h2 className="font-display text-subhead">{t('public.replying')}</h2>
        <p className="max-w-prose text-body text-leaf-soft">
          {site.data.rsvpOpen ? t('public.useYourLink') : t('public.repliesClosed')}
        </p>
        {content.rsvpDeadline && site.data.rsvpOpen && (
          <p className="tabular text-caption text-leaf-soft">
            {t('public.replyBy', { date: formatLongDate(content.rsvpDeadline) })}
          </p>
        )}
      </section>
    </Shell>
  );
}

function Section({ heading, body }: { heading: string; body: string }) {
  return (
    <section className="flex flex-col gap-4 border-b border-line py-12">
      <h2 className="font-display text-subhead">{heading}</h2>
      <p className="max-w-prose whitespace-pre-line text-body leading-relaxed text-leaf-soft">
        {body}
      </p>
    </section>
  );
}

export function Shell({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation();
  return (
    <div className="min-h-screen bg-paper">
      <main className="gutter mx-auto w-full max-w-[720px] py-16">{children}</main>
      <footer className="gutter mx-auto flex w-full max-w-[720px] items-center gap-2 pb-[max(2rem,env(safe-area-inset-bottom))] text-caption text-leaf-soft">
        <Logo size={16} tone="leaf" />
        <span>{t('public.madeWith')}</span>
      </footer>
    </div>
  );
}

/** `2028-02-19` as `19 February 2028`, parsed as UTC so it cannot slip a day. */
export function formatLongDate(date: string): string {
  const [y, m, d] = date.split('-').map(Number);
  if (!y || !m || !d) return date;
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
}
